/**
 * Hex Encode Tool
 *
 * Encode text to hexadecimal representation using browser-native TextEncoder.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import type { ITransformerTool } from '@/interfaces/ITool';

export const HexEncodeTool: ITransformerTool = {
  id: 'hex-encode',
  name: 'Hex Encode',
  description: 'Encode text to hexadecimal',
  version: '1.0.0',
  type: 'transformer',
  category: 'Encoders & Decoders',
  keywords: ['hex', 'hexadecimal', 'encode', 'text to hex'],

  seo: {
    slug: 'hex-encoder',
    pageTitle: 'Hex Encoder - Free Online Tool | Prism',
    metaDescription: 'Encode text to hexadecimal representation. Supports UTF-8 input with configurable separator. Fast, private, runs entirely in your browser.',
    keywords: ['hex encoder', 'text to hex', 'hexadecimal encoder', 'hex encode online', 'string to hex'],
    relatedTools: ['hex-decode', 'base64-encode', 'url-encode'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.HEX,

  attributes: [
    {
      name: 'separator',
      type: 'select',
      label: 'Byte Separator',
      defaultValue: '',
      options: ['', ' ', ':', '0x'],
      description: 'Separator between hex bytes (none, space, colon, 0x prefix)',
    },
    {
      name: 'uppercase',
      type: 'boolean',
      label: 'Uppercase',
      defaultValue: false,
      description: 'Use uppercase hex characters (A-F)',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const text = String(input ?? '');
    if (!text) return '';

    const separator = String(config.separator ?? '');
    const uppercase = config.uppercase === true;
    const encoder = new TextEncoder();
    const bytes = encoder.encode(text);

    const hexBytes = Array.from(bytes).map((byte) => {
      const hex = byte.toString(16).padStart(2, '0');
      return uppercase ? hex.toUpperCase() : hex;
    });

    if (separator === '0x') {
      return hexBytes.map((h) => `0x${h}`).join(' ');
    }

    return hexBytes.join(separator);
  },
};
